import React from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import styled from '@mui/material/styles/styled';
import Typography from '@mui/material/Typography';

import InfoIcon from '~/assets/images/info-icon.svg';
import Tooltip from '~/components/CommonTooltip';
import CopyWithTooltip from '~/components/CopyWithTooltip';

interface InfoCardProps {
  title: string;
  value?: React.ReactNode | string[];
  action?: React.ReactNode;
  tooltip?: string;
  tooltipIconSize?: number;
  dataTestId?: string;
  showCopyIcon?: boolean;
}
const StyledCardActions = styled(CardActions)(({ theme }) => ({
  marginTop: 'auto',
  paddingTop: 0,
  paddingLeft: 0,
  paddingRight: 0,
  display: 'flex',
  justifyContent: 'flex-start',
  gap: theme.spacing(1),
  flexShrink: 0,
}));

const InfoCard: React.FC<InfoCardProps> = ({
  title,
  value = undefined,
  action = undefined,
  tooltip = undefined,
  tooltipIconSize = 24,
  showCopyIcon = false,
  dataTestId = 'info-card',
}) => {
  const renderValues = () => {
    if (
      value === null ||
      value === undefined ||
      (typeof value !== 'string' && !Array.isArray(value))
    ) {
      return value;
    }

    const items = Array.isArray(value) ? value : [value];

    return (
      <Stack spacing={1}>
        {items.map((text) => (
          <Stack key={text} direction="row" alignItems="center" spacing={1}>
            <Typography
              variant="body2"
              sx={{ userSelect: 'text', wordBreak: 'break-word' }}
            >
              {text}
            </Typography>
            {showCopyIcon && <CopyWithTooltip text={text} />}
          </Stack>
        ))}
      </Stack>
    );
  };

  return (
    <Card
      data-testid={dataTestId}
      sx={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}
      variant="outlined"
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ flexShrink: 0 }}
      >
        <Typography
          variant="h3"
          sx={{
            flexGrow: 1,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            py: '2px',
          }}
        >
          {title}
        </Typography>
        {tooltip && (
          <Tooltip title={tooltip} arrow placement="top">
            <IconButton size="small" data-testid="info-icon">
              <Box
                component="img"
                src={InfoIcon}
                alt="Info Icon"
                width={tooltipIconSize}
                height={tooltipIconSize}
              />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      {value && <CardContent>{renderValues()}</CardContent>}

      {action && <StyledCardActions>{action}</StyledCardActions>}
    </Card>
  );
};

export default InfoCard;
